import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileSpreadsheet, FileText, FileDown, BarChart3 } from "lucide-react";
import { API, authHeaders, inputCls } from "./shared";

const MONEY_COLS = new Set(["Amount", "Total", "Income", "Expenses", "Net", "Expected", "Actual",
  "Difference", "Balance Due", "Paid", "Booking Total", "Base Payment", "Processing Fee Collected",
  "Total Charged", "Actual Stripe/Square Fee", "Net Received"]);
const fmtCell = (col, v) => (MONEY_COLS.has(col) && typeof v === "number")
  ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : v;

export default function ReportsModule({ token, onAuthError }) {
  const [reports, setReports] = useState([]);
  const [rtype, setRtype] = useState("pnl");
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const [from, setFrom] = useState(yearStart);
  const [to, setTo] = useState("");
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    axios.get(`${API}/finance/reports`, authHeaders(token)).then((r) => setReports(r.data.reports || []))
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); });
    // eslint-disable-next-line
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    axios.get(`${API}/finance/reports/${rtype}?${params}`, authHeaders(token))
      .then((r) => setRep(r.data))
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); else toast.error("Could not load report"); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [rtype, from, to]);
  useEffect(() => { load(); }, [load]);

  const doExport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams({ format });
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      const res = await fetch(`${API}/finance/reports/${rtype}/export?${params}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `autohire-${rtype}${from ? `-${from}` : ""}${to ? `_${to}` : ""}.${format === "excel" ? "xlsx" : format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
    finally { setExporting(null); }
  };

  return (
    <div className="space-y-4" data-testid="reports-module">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-white/45 block mb-1">Report</label>
          <select data-testid="report-type-select" value={rtype} onChange={(e) => setRtype(e.target.value)} className={`${inputCls} !w-64`}>
            {reports.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-white/45 block mb-1">From</label>
          <input data-testid="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${inputCls} !w-auto`} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-white/45 block mb-1">To</label>
          <input data-testid="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${inputCls} !w-auto`} />
        </div>
        <div className="flex gap-2 ml-auto">
          <button data-testid="export-csv" onClick={() => doExport("csv")} disabled={!!exporting}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-white/70 text-xs font-semibold hover:border-[#F2B705]/60 hover:text-white disabled:opacity-50">
            {exporting === "csv" ? <Loader2 className="animate-spin" size={13} /> : <FileDown size={13} />} CSV
          </button>
          <button data-testid="export-excel" onClick={() => doExport("xlsx")} disabled={!!exporting}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-white/70 text-xs font-semibold hover:border-[#F2B705]/60 hover:text-white disabled:opacity-50">
            {exporting === "xlsx" ? <Loader2 className="animate-spin" size={13} /> : <FileSpreadsheet size={13} />} Excel
          </button>
          <button data-testid="export-pdf" onClick={() => doExport("pdf")} disabled={!!exporting}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#F2B705] text-black text-xs font-bold hover:bg-white disabled:opacity-50">
            {exporting === "pdf" ? <Loader2 className="animate-spin" size={13} /> : <FileText size={13} />} PDF
          </button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-14"><Loader2 className="animate-spin text-[#F2B705]" /></div> : rep && (
        <div className="bg-[#0C0C0C] border border-white/10 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <BarChart3 size={15} className="text-[#F2B705]" />
            <span className="text-white font-bold">{rep.title}</span>
            <span className="text-white/35 text-xs">{rep.count} row(s)</span>
            <span className="ml-auto text-xs text-white/45">
              {Object.entries(rep.totals || {}).map(([k, v]) => (
                <span key={k} className="ml-3">{k}: <span className="text-[#F2B705] font-bold">${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></span>
              ))}
            </span>
          </div>
          <div className="overflow-x-auto max-h-[540px] overflow-y-auto">
            <table className="w-full text-sm" data-testid="report-table">
              <thead className="sticky top-0 bg-[#111]">
                <tr>{rep.columns.map((c) => <th key={c} className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-white/40 whitespace-nowrap">{c}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {rep.rows.length === 0 && <tr><td colSpan={rep.columns.length} className="px-4 py-6 text-white/35">No data for this period.</td></tr>}
                {rep.rows.map((r, i) => (
                  <tr key={i} className="hover:bg-white/[0.02]">
                    {r.map((v, j) => (
                      <td key={j} className={`px-4 py-2 whitespace-nowrap ${MONEY_COLS.has(rep.columns[j]) ? "text-green-400 font-semibold" : "text-white/75"}`}>
                        {fmtCell(rep.columns[j], v)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
